<script lang="ts">
	import Breadcrumbs from '$lib/components/ui/Breadcrumbs.svelte';
	import CoordsDecoration from '$lib/components/ui/CoordsDecoration.svelte';
	import ImageGallery from '$lib/components/ui/ImageGallery.svelte';
	import type { BreadcrumbLink } from '$lib/features/_other/breadcrumbLink';
	import type { Coordinates } from '$lib/features/_other/coordinates.type';
	import type { ImageDetails } from '$lib/features/image/imageDetails.type';
	import { UrlUtil, type NavLink } from '$lib/utils/url.util';
	import type { Component, Snippet } from 'svelte';
	import type { ClassValue } from 'svelte/elements';

	const {
		breadcrumbs,
		header,
		imageGallery,
		decorations,
		navsToOtherPages = [],
		QuickStatsSnippet
	}: {
		breadcrumbs: BreadcrumbLink[];
		header: {
			title: string;
			subtitle?: string;
			description: string;
		};
		imageGallery: {
			images: ImageDetails[];
			internalClasses: ClassValue;
			ariaLabel: string;
		};
		decorations?: {
			coordinates?: Coordinates[];
		};
		navsToOtherPages?: { Icon: Component; label: string; nav: NavLink; ariaLabel?: string }[];
		QuickStatsSnippet: Snippet;
	} = $props();
</script>

<section class="relative flex min-h-screen flex-col overflow-hidden bg-black py-17 lg:flex-row lg:flex-wrap">
	<div class="absolute inset-0 z-0">
		{@render Backdrop()}
	</div>

	<div class="relative z-10 flex w-full flex-col justify-center gap-4 px-6 py-4 text-white md:pl-12 lg:w-1/2 lg:pl-24">
		<Breadcrumbs items={breadcrumbs} textColour="white" />

		<header>
			{#if header.subtitle}
				<hgroup>
					{@render Title()}
					<p class="mt-1 text-white/70">
						{header.subtitle}
					</p>
				</hgroup>
			{:else}
				{@render Title()}
			{/if}
		</header>

		<div class="flex flex-wrap items-center gap-3 text-white/70">
			{@render QuickStatsSnippet()}
		</div>

		<p class="text-lg font-light text-white/90">
			{header.description}
		</p>
	</div>

	<div class="relative z-10 flex w-full items-center justify-center p-4 lg:h-auto lg:w-1/2">
		<ImageGallery
			classes="max-w-xl"
			internalClasses={imageGallery.internalClasses}
			images={imageGallery.images}
			ariaLabel={imageGallery.ariaLabel}
			eagerLoad={true}
		/>
	</div>

	<div class="relative z-10 w-full px-6 pb-6 md:px-12 lg:px-24">
		{@render OtherLinksNav()}
	</div>

	<CoordsDecoration coordinates={decorations?.coordinates ?? []} classes="text-white/70" />
</section>

{#snippet Title()}
	<h1 class="font-serif text-5xl lg:text-7xl">
		{header.title}
	</h1>
{/snippet}

{#snippet Backdrop()}
	{const firstImage = $derived<ImageDetails | undefined>(imageGallery.images[0])}
	{#if firstImage}
		<div class="absolute inset-0 overflow-hidden bg-black">
			<img
				class="absolute inset-0 h-full w-full scale-110 object-cover blur-lg select-none"
				src={UrlUtil.getFullImageUrl(firstImage)}
				alt={firstImage.altText}
				width={firstImage.width}
				height={firstImage.height}
				decoding="async"
			/>
			<div class="absolute inset-0 bg-black/70"></div>
		</div>
	{/if}
{/snippet}

{#snippet OtherLinksNav()}
	{#if navsToOtherPages.length > 0}
		<ul class="flex flex-wrap items-center gap-2">
			{#each navsToOtherPages as item (item.label)}
				<li>
					<a
						class="btn btn-soft font-mono shadow-sm btn-primary btn-sm"
						href="/{item.nav.href}"
						data-sveltekit-preload-data={item.nav.preloadData}
						data-sveltekit-preload-code={item.nav.preloadCode}
						aria-label={item.ariaLabel}
					>
						<item.Icon class="text-lg" />
						{item.label}
					</a>
				</li>
			{/each}
		</ul>
	{/if}
{/snippet}
